import styles from "../../page.module.css";

import { inject, observer } from "mobx-react";
import { ClientOverviewStore, ViewType } from "../../store/store";
import { ViewItem, ViewSwitch } from "../../../../components/view-switch";
import CardViewGrid from "../card-view-grid/CardViewGrid";
import ResidentStatusGrid from "../resident-status-grid/ResidentStatusGrid";
import { CARD_TYPES } from "../card-view-grid/card-item/card-item";
import webResource from "../../web";
import { ClientDataConverter } from "../../store/convertor";
import { runInAction } from "mobx";
import { ClientDataIn } from "../../store/typesIn";
import { useEffect, useRef } from "react";
import { FeatureStore, TransitionStore } from "../../../../store";
import { useTranslation } from "react-i18next";
import { GlobalDialogData } from "../../../../store/transition-store";
import {
    NO_CONNECTION_MODAL_ID,
    NoConnectionModal,
} from "../../../../utils/NoConnectionDialog";
import LocationSelectorStore from "../../../../store/location-selector-store";
import { Moment } from "moment";
import moment from "moment";
import UserSettingsStore, {
    SortSettings,
    UserSettings,
} from "../../../../store/user-settings";

const GRID_REFRESH_RATE_MS = 7500;

export interface ResidentStatusTabProps {
    clientOverviewStore?: ClientOverviewStore;
    transitionStore?: TransitionStore;
    featureStore?: FeatureStore;
    locationSelectorStore?: LocationSelectorStore;
    userSettingsStore?: UserSettingsStore;
}

function ResidentStatusTab(props: ResidentStatusTabProps) {
    const clientOverviewStore = props.clientOverviewStore!;
    const transitionStore = props.transitionStore!;
    const featureStore = props.featureStore!;
    const locationSelectorStore = props.locationSelectorStore!;
    const userSettingsStore = props.userSettingsStore!;

    const { initialized, requestIds } = locationSelectorStore;
    const sortOptions = userSettingsStore.getUserSetting(
        SortSettings.RESIDENT_STATUS
    );

    let gridRefreshTimer = useRef<any>(undefined);
    let lastRefreshTimestamp = useRef<Moment | null>(null);

    const { t } = useTranslation("client-grid");

    useEffect(() => {
        gridRefreshTimer.current = setInterval(silentFetchData, 500);
        lastRefreshTimestamp.current = moment();

        return () => {
            if (gridRefreshTimer) {
                clearInterval(gridRefreshTimer.current!);
                gridRefreshTimer.current = undefined;
            }
        };
    }, []);

    useEffect(() => {
        if (initialized) {
            clientOverviewStore.setIsDataLoading(true);

            lastRefreshTimestamp.current = moment();
            fetchData()
                .then(() => {
                    if (transitionStore.id === NO_CONNECTION_MODAL_ID) {
                        transitionStore.hideGlobalDialog();
                    }
                    clientOverviewStore.executeOnReload();
                    scrollIntoViewSelectedClient();
                })
                .catch((ajaxError: any) => {
                    const dialog: GlobalDialogData = NoConnectionModal(t);

                    transitionStore?.showGlobalDialog(dialog);
                })
                .finally(() => {
                    clientOverviewStore.setIsDataLoading(false);
                });
        }
    }, [sortOptions, requestIds, initialized]);

    const scrollIntoViewSelectedClient = () => {
        setTimeout(() => {
            const selectedClientElement =
                document.querySelector("#selected-client");
            if (selectedClientElement) {
                selectedClientElement.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                    inline: "start",
                });
            }
        });
    };

    const validateStaleData = () => {
        if (!clientOverviewStore.isDataStale) {
            transitionStore.setGlobalWarningMessage("");
            return;
        }

        if (featureStore.featureList.staleDataMessageVisible)
            transitionStore.setGlobalWarningMessage(t("StaleDataMessage"));
    };

    const silentFetchData = () => {
        if (lastRefreshTimestamp) {
            if (
                moment().diff(lastRefreshTimestamp.current, "milliseconds") >=
                GRID_REFRESH_RATE_MS
            ) {
                fetchData();
                lastRefreshTimestamp.current = moment();
            }
        }
    };

    const fetchData = () => {
        const { requestIds } = locationSelectorStore;
        
        const sortOptions = userSettingsStore.getUserSetting(
            SortSettings.RESIDENT_STATUS
        );

        const payload = {
            SortOptions: [sortOptions],
            FilteredLocationIds: requestIds,
        };

        return webResource.readMyClients(payload).then((ajaxResponse) => {
            const response = ajaxResponse.data!;
            const { assignedClientsForNurse, isDataStale } = response;

            const clientList = assignedClientsForNurse.map(
                (client: ClientDataIn) => {
                    return ClientDataConverter.toStore(client);
                }
            );

            runInAction(() => {
                clientOverviewStore.clients = clientList;
                clientOverviewStore.isDataStale = isDataStale;
            });
            validateStaleData();
        });
    };

    const render = () => {
        return (
            <ViewSwitch
                activeKey={userSettingsStore.getUserSetting(
                    UserSettings.RESIDENT_VIEW_TYPE
                )}
            >
                <ViewItem itemKey={[ViewType.LIST]}>
                    <div className={styles["overflow-content"]}>
                        <ResidentStatusGrid />
                    </div>
                </ViewItem>
                <ViewItem itemKey={[ViewType.GRID]}>
                    <div className={styles["overflow-content"]}>
                        <CardViewGrid cardType={CARD_TYPES.Status} />
                    </div>
                </ViewItem>
            </ViewSwitch>
        );
    };

    return render();
}

export default inject(
    "clientOverviewStore",
    "transitionStore",
    "featureStore",
    "locationSelectorStore",
    "userSettingsStore"
)(observer(ResidentStatusTab));
